/**
 * EPO File Generation Service
 *
 * Handles generation and download of EPO payment files for expense reports
 */

import { fetchWithAuth } from './api-client';
import { showToast } from '../components/toast';

/**
 * Download EPO file for an expense report
 */
export async function downloadEpoFile(reportId: string): Promise<void> {
  try {
    const response = await fetchWithAuth(`/api/expense-reports/${reportId}/epo`, {
      method: 'GET',
      headers: {
        // Explicitly request XML content
        'Accept': 'application/xml',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);

      // Handle specific error cases
      if (response.status === 403) {
        throw new Error('You do not have permission to generate EPO files');
      }

      if (response.status === 400) {
        if (errorData?.error?.includes('payment profile')) {
          throw new Error('Employee payment profile is incomplete. Please contact the employee to update their IBAN.');
        }
        if (errorData?.error?.includes('SUBMITTED')) {
          throw new Error('EPO can only be generated for submitted reports');
        }
        throw new Error(errorData?.error || 'Invalid request');
      }

      if (response.status === 500) {
        if (errorData?.error?.includes('Company')) {
          throw new Error('Company payment details not configured. Please contact your administrator.');
        }
        if (errorData?.error?.includes('validation')) {
          throw new Error('Generated EPO file failed validation. Please try again or contact support.');
        }
      }

      throw new Error(errorData?.error || `Failed to generate EPO file (${response.status})`);
    }

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
    const filename = filenameMatch?.[1] ?? `expense-payment-${reportId.slice(0, 8)}.xml`;

    // Download the file
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();

    // Cleanup
    setTimeout(() => {
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }, 100);

    showToast('EPO file downloaded successfully', 'success');
  } catch (error) {
    console.error('Failed to download EPO file:', error);
    showToast(error instanceof Error ? error.message : 'Failed to generate EPO file', 'error');
    throw error;
  }
}

/**
 * Check if user can generate EPO files
 */
export function canGenerateEpo(userRole: string | null | undefined): boolean {
  return userRole === 'ASSISTANT' || userRole === 'ADMIN';
}